<template>
    <div>
        <!-- Banner: Desktop layout -->
        <div
            v-if="showBanner"
            class="hidden md:flex w-full p-4 items-center gap-8 rounded-lg border border-[#FEAF9F] bg-white shadow-sm"
            :style="{
                background:
                    'linear-gradient(66deg, rgba(253, 96, 29, 0.00) 25.49%, rgba(253, 96, 29, 0.12) 77.35%, rgba(253, 96, 29, 0.00) 112.21%), #FFF',
            }"
        >
            <img
                src="/images/freemium-banner-promostar.png"
                alt="Upgrade"
                class="w-9 h-9 shrink-0"
            />
            <div class="flex flex-col items-start gap-1 flex-1">
                <h3 class="text-slate-800 text-base font-medium leading-6 m-0">
                    Unlock transfer bonus alerts
                </h3>
                <p
                    class="text-slate-500 text-sm font-normal leading-5 m-0 self-stretch"
                >
                    Get notified about transfer bonuses for {{ programName }}.
                    Upgrade to 10xTravel+ to enable alerts.
                </p>
            </div>
            <div class="flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    class="flex py-2 px-4 items-start gap-2 rounded bg-transparent text-slate-800 text-sm font-medium leading-5 cursor-pointer transition-colors hover:bg-gray-100"
                    @click="dismissBanner"
                >
                    Dismiss
                </button>
                <button
                    type="button"
                    class="flex py-2 px-4 justify-center items-center gap-2 rounded bg-orange text-white text-sm font-medium leading-5 cursor-pointer shadow-sm transition-colors hover:bg-orange-600"
                    @click="handleUpgrade"
                >
                    Upgrade
                </button>
            </div>
        </div>

        <!-- Banner: Mobile layout -->
        <div
            v-if="showBanner"
            class="flex md:hidden w-full p-4 flex-col items-center gap-8 rounded-lg border border-[#FEAF9F] shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_0_rgba(0,0,0,0.06)]"
            :style="{
                background:
                    'radial-gradient(95.84% 84.35% at 22.45% 20.06%, rgba(253, 96, 29, 0.00) 29.86%, rgba(253, 96, 29, 0.12) 90.75%), #FFF',
            }"
        >
            <div class="flex w-full items-start gap-3">
                <img
                    src="/images/freemium-banner-promostar.png"
                    alt="Upgrade"
                    class="w-9 h-9 shrink-0"
                />
                <div class="flex flex-col justify-center items-start flex-1">
                    <div class="flex flex-col items-start gap-1">
                        <h3
                            class="text-slate-800 text-base font-medium leading-6 m-0"
                        >
                            Unlock transfer bonus alerts
                        </h3>
                        <p
                            class="text-slate-500 text-sm font-normal leading-5 m-0"
                        >
                            Get notified about transfer bonuses. Upgrade to
                            10xTravel+ to enable alerts.
                        </p>
                    </div>
                    <div class="flex items-center gap-2 pt-4">
                        <button
                            type="button"
                            class="flex py-2 px-4 justify-center items-center gap-2 rounded bg-orange text-white text-sm font-medium leading-5 cursor-pointer shadow-sm transition-colors hover:bg-orange-600"
                            @click="handleUpgrade"
                        >
                            Upgrade
                        </button>
                        <button
                            type="button"
                            class="flex py-2 px-4 items-center gap-2 rounded bg-transparent text-slate-800 text-sm font-medium leading-5 cursor-pointer transition-colors hover:bg-gray-100"
                            @click="dismissBanner"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Upgrade Modal -->
        <UpgradeModal
            :show="showUpgradeModal"
            description="Transfer bonus alerts are part of the 10xTravel+ plan. Upgrade to unlock them and get full access."
            @close="showUpgradeModal = false"
        />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';
import UpgradeModal from '@/modals/upgrade-modal.vue';

const props = defineProps({
    programName: { type: String, default: 'your favorite programs' },
    // Whether the user previously dismissed this banner. Persisted globally
    // per-user via user_settings.bonus_alerts_banner_dismissed, so once
    // dismissed it stays hidden across sessions and all program pages.
    bannerDismissed: { type: Boolean, default: false },
    dismissUrl: { type: String, default: null },
});

const dismissed = ref(false);
const showUpgradeModal = ref(false);

const showBanner = computed(() => !dismissed.value && !props.bannerDismissed);

// Fire-and-forget axios POST (endpoint returns 204). Matches the campaign
// modal dismissal pattern; using Inertia's router.post would interpret the
// 204 as an empty page and render a blank overlay.
const dismissBanner = () => {
    dismissed.value = true;
    if (props.dismissUrl) {
        axios.post(props.dismissUrl).catch(() => {
            // Best-effort: if persistence fails the banner stays hidden for
            // this session and reappears on the next page load.
        });
    }
};

const handleUpgrade = () => {
    showUpgradeModal.value = true;
};
</script>
